import React from 'react'
// libs
import { reduxForm } from 'redux-form'
import { useRouter } from 'hooks/useRouter'
import { useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client/react'
// redux
import { setAccumulatedFormData } from 'store/reducers/accumulatedForms'
// hooks
import { useFileUpload } from 'hooks/useFileUpload'
import { useCurrentUser, useShowModal } from 'hooks'
// components
import {
  ReduxInput,
  ReduxSelect,
  ReduxDatePicker,
  ReduxGeosuggest,
  ReduxBusinessDaysSelect
} from 'components/FormElements/ReduxForm'
import BottomPaper from '../BottomPaper'
import AvatarUpload from 'components/PageComponents/Settings/Users/Edit/Form/components/AvatarUpload'
import ImageCropper from 'components/Modals/ImageCropperModal'
import InputAdornment from '@mui/material/InputAdornment'
import SetDayWorkingHoursModal from 'components/Modals/SetDayWorkingHoursModal'
// constants
import { Forms, Modals } from 'constants/index'
import { SECTOR_OPTIONS } from 'constants/sectors'
import { LANGUAGE_OPTIONS } from 'constants/countries'
import { CURRENCIES_OPTIONS } from 'constants/currencies'
import { BUSINESS_DAYS_OPTIONS, getDateTimeFormatOptions } from 'constants/date'
// types
import { AssetType } from 'types/graphql'
import { InjectedFormProps } from 'redux-form/lib/reduxForm'
// themes
import { Form, FormInnerWrapper } from 'themes/views/settings'
// graphql
import { COMPANY_UPDATE_MUTATION } from 'graphql/company/mutations'
// store
import { setCurrentUser } from 'store/reducers/currentUser'
// views
import { TopSection, TopSectionContainer, MiddleSection, BottomSection } from './views'
// utils
import moment from 'moment-timezone'
import { required } from 'utils/validators'
import { getFileParams } from 'utils/upload'
import { generateTimezoneOptions } from 'utils/date'

interface Props {
  companyId: string
  goNextTab: () => void
  prepareForApi: (data: any) => any
  handleCancelClick: () => void
}

const AccountDetailsForm: React.FC<InjectedFormProps<any, Props> & Props> = ({
  goNextTab,
  companyId,
  handleSubmit,
  prepareForApi,
  initialValues,
  handleCancelClick
}) => {
  const user = useCurrentUser()
  const router = useRouter()
  const dispatch = useDispatch()

  const [companyUpdateMutation] = useMutation(COMPANY_UPDATE_MUTATION)

  const { upload } = useFileUpload()

  const { showModal } = useShowModal({ modalType: Modals.IMAGE_CROPPER_MODAL })

  const [logo, setLogo] = React.useState<any>({})

  const isUpdate = !!router.query.id

  const onLogoUpload = (files) => {
    const file = files[0]

    if (!file) {
      return
    }

    const reader = new FileReader()

    reader.onload = () => {
      const fileAsBinaryString = reader.result

      showModal({
        width: 'md',
        image: fileAsBinaryString,
        handleCropImage: (blob: Blob, url: string) => setLogo({ blob, url, type: file.type, changed: true })
      })
    }

    reader.onabort = console.log
    reader.onerror = console.log

    reader.readAsDataURL(files[0])
  }

  const handleFormSubmit = async (data) => {
    try {
      const additionalData: any = {}

      if (logo.changed) {
        const downloadUrl = await upload(logo.blob, {
          file: getFileParams({
            assetType: AssetType.CompanyLogo,
            contentType: logo.type,
            id: companyId
          })
        })

        additionalData.logoUrl = downloadUrl
      }

      if (isUpdate) {
        const preparedCompany = prepareForApi({ ...data, ...additionalData })

        await companyUpdateMutation({
          variables: {
            id: router.query.id,
            company: preparedCompany
          }
        })

        if (companyId === user.companyId) {
          dispatch(setCurrentUser({ ...user, company: preparedCompany }))
        }

        handleCancelClick()
      } else {
        dispatch(
          setAccumulatedFormData({
            type: Forms.ACCUMULATED_CREATE_ACCOUNT,
            data: { ...data, ...additionalData }
          })
        )
        goNextTab()
      }
    } catch (e) {
      console.log('[AccountDetailsForm.handleFormSubmit]: ', e)
    }
  }

  return (
    <>
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <FormInnerWrapper>
          <TopSection>
            <TopSectionContainer>
              <ReduxInput label='Account Name' name='name' validate={[required]} />
              <ReduxInput label='Account Code' name='code' validate={[required]} />
              <ReduxInput
                label='Domain'
                name='domainName'
                validate={[required]}
                endAdornment={<InputAdornment position='end'>.skefto.com</InputAdornment>}
              />
              <ReduxSelect label='Language' name='language' options={LANGUAGE_OPTIONS} validate={[required]} />
            </TopSectionContainer>
            <AvatarUpload
              onUpload={onLogoUpload}
              description='Change your company logo'
              avatar={logo.url || initialValues.logoUrl}
            />
          </TopSection>

          <MiddleSection>
            <ReduxDatePicker label='Financial Year Start' name='financialYearStartsAt' validate={[required]} />
            <ReduxDatePicker label='Financial Year End' name='financialYearFinishesAt' validate={[required]} />
            <ReduxSelect
              label='Date & Number Format'
              name='dateAndNumberFormat'
              options={getDateTimeFormatOptions()}
              validate={required}
            />
            <ReduxSelect isMultiple name='sector' label='Sector' options={SECTOR_OPTIONS} validate={[required]} />
            <ReduxSelect label='Timezone' name='timezone' options={generateTimezoneOptions()} validate={[required]} />
            <ReduxGeosuggest label='Address' name='address' validate={[required]} />
            <ReduxBusinessDaysSelect
              name='businessDays'
              label='Business Days'
              validate={[required]}
              options={BUSINESS_DAYS_OPTIONS}
            />
          </MiddleSection>

          <BottomSection>
            <ReduxInput label='Business Registration Number' name='businessNumber' />
            <ReduxInput label='Company Registration Number' name='companyNumber' />
            <ReduxSelect name='currency' label='Currency' options={CURRENCIES_OPTIONS} />
          </BottomSection>
        </FormInnerWrapper>

        <BottomPaper onCancelClick={handleCancelClick} />
      </Form>
      <ImageCropper />
      <SetDayWorkingHoursModal />
    </>
  )
}

export default reduxForm<any, Props>({
  form: Forms.ACCOUNT_DETAILS_FORM,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  initialValues: {
    sector: [],
    financialYearStartsAt: moment().startOf('year').toISOString(),
    financialYearEndsAt: moment().endOf('year').toISOString()
  }
})(AccountDetailsForm)
